import React, { useEffect, useMemo, useState } from "react";
import { Wallet, TrendingUp, IndianRupee, Clock, Users, Filter, Calendar, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function AgentEarnings() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("commissions"); // commissions | payments
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => { api.get("/agent/earnings/detail").then((r) => setData(r.data)); }, []);

  const inRange = (iso) => {
    if (!from && !to) return true;
    const t = new Date(iso).getTime();
    if (from && t < new Date(from).getTime()) return false;
    if (to && t > new Date(to).getTime() + 24 * 3600 * 1000) return false;
    return true;
  };

  const collections = useMemo(() => (data?.collections || []).filter((c) => inRange(c.created_at)), [data, from, to]);
  const payments = useMemo(() => (data?.payments || []).filter((p) => inRange(p.created_at)), [data, from, to]);

  const filteredCommission = collections.reduce((s, c) => s + (c.commission || 0), 0);
  const filteredCollection = collections.reduce((s, c) => s + (c.collection_amount || 0), 0);

  if (!data) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div data-testid="agent-earnings-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">My Earnings</h1>
        <p className="text-sm text-slate-500">Lifetime commissions, payments received & balance to receive</p>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard label="Total Earned" value={fmtINR(data.total_earned)} icon={Wallet} grad="emerald-grad" testId="agent-total-earned" />
        <SummaryCard label="Total Paid" value={fmtINR(data.total_paid)} icon={ArrowDownToLine} grad="bg-tenant-gradient" testId="agent-total-paid" />
        <SummaryCard label="Balance to Receive" value={fmtINR(data.balance_to_receive)} icon={Clock} grad="bg-amber-500" testId="agent-balance" />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <Mini label="Commission Rate" value={`${data.commission_rate}%`} icon={TrendingUp} />
        <Mini label="Total Collected" value={fmtINR(data.total_collected)} icon={IndianRupee} />
        <Mini label="Collections" value={String(data.total_collections)} icon={Users} />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <div className="flex bg-white border border-slate-200 rounded-xl p-1">
          <button data-testid="tab-commissions" onClick={() => setTab("commissions")} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg uppercase tracking-wider ${tab === "commissions" ? "bg-tenant-primary text-white" : "text-slate-500"}`}>
            Commissions ({collections.length})
          </button>
          <button data-testid="tab-payments" onClick={() => setTab("payments")} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg uppercase tracking-wider ${tab === "payments" ? "bg-tenant-primary text-white" : "text-slate-500"}`}>
            Payments ({payments.length})
          </button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <DateInput value={from} onChange={setFrom} placeholder="From" testId="earnings-from" />
          <DateInput value={to} onChange={setTo} placeholder="To" testId="earnings-to" />
          {(from || to) && (
            <button data-testid="clear-filter" onClick={() => { setFrom(""); setTo(""); }} className="text-[11px] font-bold text-slate-500 underline">Clear</button>
          )}
        </div>
      </div>

      {(from || to) && (
        <div className="mt-3 bg-amber-50/70 border border-amber-200 rounded-xl px-3 py-2 text-[11px] text-amber-700">
          Filtered: Commission <b>{fmtINR(filteredCommission)}</b> on <b>{fmtINR(filteredCollection)}</b> collection across {collections.length} entries.
        </div>
      )}

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        {tab === "commissions" ? (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Customer</th>
                <th className="text-left p-3">Method</th>
                <th className="text-right p-3">Collection</th>
                <th className="text-right p-3">Commission</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {collections.map((c) => (
                <tr key={c.id} data-testid={`earn-row-${c.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(c.created_at)}</td>
                  <td className="p-3">
                    <div className="font-bold text-[13px]">{c.customer_name || "—"}</div>
                    <div className="text-[10px] text-slate-400">+91 {c.customer_phone}</div>
                  </td>
                  <td className="p-3 text-[11px] uppercase tracking-wider text-slate-500">{c.method}</td>
                  <td className="p-3 text-right font-bold">{fmtINR(c.collection_amount)}</td>
                  <td className="p-3 text-right font-extrabold text-emerald-600">+{fmtINR(c.commission)}</td>
                </tr>
              ))}
              {collections.length === 0 && (
                <tr><td colSpan={5} className="text-center p-6 text-slate-400">No commission entries.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Method</th>
                <th className="text-left p-3">Remarks</th>
                <th className="text-right p-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} data-testid={`pay-row-${p.id}`} className="hover:bg-slate-50">
                  <td className="p-3 text-[12px]">{fmtDate(p.created_at)}</td>
                  <td className="p-3 text-[11px] uppercase tracking-wider text-slate-500">{p.method}</td>
                  <td className="p-3 text-[12px] text-slate-600">{p.remarks || "—"}</td>
                  <td className="p-3 text-right font-extrabold text-blue-600">{fmtINR(p.amount)}</td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={4} className="text-center p-6 text-slate-400">No payments received yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, grad, testId }) => (
  <div className={`${grad} text-white rounded-2xl p-5 card-shadow`}>
    <Icon className="w-6 h-6" />
    <div className="text-[10px] uppercase tracking-[0.25em] opacity-90 font-bold mt-2">{label}</div>
    <div data-testid={testId} className="text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);

const Mini = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-slate-100 rounded-xl p-3 card-shadow">
    <Icon className="w-4 h-4 text-slate-400" />
    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-400 mt-1">{label}</div>
    <div className="text-base font-extrabold mt-0.5">{value}</div>
  </div>
);

const DateInput = ({ value, onChange, placeholder, testId }) => (
  <div className="relative">
    <Calendar className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
    <input data-testid={testId} type="date" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-6 pr-2 py-1.5 text-[11px] border border-slate-200 rounded-lg outline-none focus:border-tenant-primary" />
  </div>
);
